import { FunctionComponent, ReactElement, ReactNode, useEffect } from 'react';
import { AxiosError } from 'axios';
import useErrors from '@/shared/context/errors-context';
import useToastMessage from '@/shared/context/toast-message-context';
import { removeAuthData } from '@/shared/utils/auth';
import { ErrorUnprocessedEntityResponse } from './types';
import { httpClient } from './http';

const AxiosInterceptors: FunctionComponent<{ children: ReactNode }> = ({
	children,
}): ReactElement => {
	const { add, clear } = useErrors();
	const { dispatch } = useToastMessage();

	useEffect(() => {
		const requestInterceptor = httpClient.interceptors.request.use((config) => {
			const formKey: string | undefined = config?.headers?.formKey as string;
			if (formKey) clear(formKey);

			return config;
		});

		const responseInterceptor = httpClient.interceptors.response.use(
			(response) => response,
			(error: AxiosError) => {
				const { config } = error;
				const headers = config?.headers;
				const formKey: string | undefined = headers?.formKey as string;

				if (error.response?.status === 500) {
					dispatch({
						title: 'External error',
						content: 'Something went wrong...',
						type: 'error',
					});
				}

				if (error.response?.status === 422 && formKey) {
					const errorUnprocessedEntity = error.response.data as ErrorUnprocessedEntityResponse;
					add(formKey, errorUnprocessedEntity.error.fields);
				}

				if (error.response?.status === 401) {
					removeAuthData();
					dispatch({
						type: 'warning',
						content: 'Your session has expired, please login',
					});
					setTimeout(() => {
						window.location.href = '/auth/login';
					}, 500);
				}

				return Promise.reject(error);
			}
		);

		return () => {
			httpClient.interceptors.request.eject(requestInterceptor);
			httpClient.interceptors.response.eject(responseInterceptor);
		};
		/* eslint-disable react-hooks/exhaustive-deps */
	}, []);

	return <>{children}</>;
};

export default AxiosInterceptors;
